/**
 * InspectorChips — horizontal scrolling pill row for inspector filtering.
 *
 * One pill per active inspector + a leading "All" pill. Click-to-toggle; the
 * selected pill drives the dashboard's `selectedInspectorId` filter. Avatars
 * fall back to a two-letter monogram in the inspector's accent colour
 * (deterministically derived from their id so it's stable across renders).
 */

import { useMemo } from 'react';
import { clsx } from 'clsx';
import type { Inspector } from '../../types';

interface Props {
  inspectors: Inspector[];
  selectedInspectorId: string | null;
  onSelect: (inspectorId: string | null) => void;
  loading?: boolean;
}

/** Stable hue per inspector id — picks one of 8 brand-adjacent hues. */
function hueFromId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  const hues = ['#7c3aed', '#06b6d4', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#3b82f6', '#ef4444'];
  return hues[Math.abs(hash) % hues.length];
}

function monogram(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function InspectorChips({ inspectors, selectedInspectorId, onSelect, loading }: Props) {
  const sorted = useMemo(
    () => [...inspectors].sort((a, b) => a.name.localeCompare(b.name)),
    [inspectors],
  );

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={clsx(
          'inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition',
          selectedInspectorId === null
            ? 'border-brand-500/40 bg-brand-500/15 text-brand-200'
            : 'border-white/10 bg-white/[0.03] text-neutral-300 hover:bg-white/[0.06]',
        )}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-brand-400" />
        All inspectors
        <span className="text-[10px] tabular-nums text-neutral-500">{inspectors.length}</span>
      </button>

      {loading && sorted.length === 0 && (
        <>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-8 w-32 shrink-0 animate-pulse rounded-full border border-white/10 bg-white/[0.04]"
            />
          ))}
        </>
      )}

      {sorted.map((ins) => {
        const active = ins.id === selectedInspectorId;
        const colour = hueFromId(ins.id);
        return (
          <button
            key={ins.id}
            type="button"
            onClick={() => onSelect(active ? null : ins.id)}
            className={clsx(
              'inline-flex shrink-0 items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium transition',
              active
                ? 'border-white/30 bg-white/10 text-white'
                : 'border-white/10 bg-white/[0.03] text-neutral-300 hover:bg-white/[0.06]',
            )}
            title={ins.email ?? ins.name}
          >
            <span
              className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white"
              style={{ backgroundColor: colour }}
            >
              {monogram(ins.name)}
            </span>
            <span className="truncate max-w-[140px]">{ins.name}</span>
            {ins.region_code && (
              <span className="rounded bg-black/30 px-1.5 py-0.5 text-[10px] text-neutral-400">
                {ins.region_code}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Exposed so other components (map pins, deal rows) can colour by inspector. */
export { hueFromId, monogram };
